import React, { useMemo } from 'react';
import { t, Trans } from '@lingui/macro';

import { ServiceType } from '@rover/constants/src/translations/service.constants';
import { Button, Flex } from '@rover/kibble/core';
import useI18nContext from '@rover/react-lib/src/context/useI18nContext';
import SearchResultsCarousel from '@rover/react-lib/src/pages/landing/components/SearchResultsCarousel';
import getSearchUrl from '@rover/react-lib/src/utils/getSearchUrl';
import type { OptimizedSearchResponse } from '@rover/rsdk/src/apiClient/latest/model/optimizedSearchResponse';
import { ServiceTypeSlugEnum } from '@rover/rsdk/src/apiClient/latest/model/serviceTypeSlugEnum';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import useCountryConfigurationShim from '@rover/shared/js/hooks/useCountryConfigurationShim';
import { storeCrossPageAnalyticsEvent } from '@rover/utilities/fireCrossPageEvent';
import { getPriceTransparencyLevel } from '@rover/utilities/priceTransparency';

import {
  CarouselPaginateAction,
  ClickSitterCard,
  SearchResultLayoutType,
  SearchResultsCtaClick,
  SitterCardType,
  ViewSitterCard,
} from '../../../landing/utilities/analyticsEvents';

type Props = {
  sitterCarouselResultsResponse: OptimizedSearchResponse;
};

const HomepageSitterCarousel: React.FC<Props> = ({ sitterCarouselResultsResponse }) => {
  const { locale } = useI18nContext();
  const { current: countryConfig } = useCountryConfigurationShim();
  const priceTransparencyLevel = getPriceTransparencyLevel(sitterCarouselResultsResponse);
  const searchResults = useMemo(
    () => sitterCarouselResultsResponse.results || [],
    [sitterCarouselResultsResponse.results]
  );
  const preferCatReviews = !!sitterCarouselResultsResponse.derivedData?.shouldPreferCatReviews;

  if (!countryConfig || !searchResults.length) {
    return null;
  }

  return (
    <Flex
      flexDirection="column"
      alignItems="flex-start"
      gap="0x"
      mb="16x"
      paddingTop={['8x', '0x']}
    >
      <SearchResultsCarousel
        searchResults={searchResults}
        heading={t`Explore highly-rated sitters in your area`}
        headingVariant="display"
        serviceType={ServiceType.OVERNIGHT_BOARDING}
        priceTransparencyLevel={priceTransparencyLevel}
        language={locale}
        preferCatReviews={preferCatReviews}
        countryConfig={countryConfig}
        onPaginate={({ pageStart, pageEnd, scrollSource }) => {
          emitAnalyticsEvent(
            new CarouselPaginateAction({
              serviceTypeSlug: ServiceTypeSlugEnum['dog-boarding'],
              pageStart,
              pageEnd,
              scrollSource,
            })
          );
        }}
        onSearchResultCardClick={() => {
          storeCrossPageAnalyticsEvent(
            new ClickSitterCard({
              serviceTypeSlug: ServiceTypeSlugEnum['dog-boarding'],
              cardType: SitterCardType.CAROUSEL,
            })
          );
        }}
        onSearchResultCardViewed={(personOpk) => {
          emitAnalyticsEvent(
            new ViewSitterCard({
              serviceTypeSlug: ServiceTypeSlugEnum['dog-boarding'],
              cardType: SitterCardType.CAROUSEL,
              sitterResultPersonOpk: personOpk,
            })
          );
        }}
      />
      <Button
        variant="primary"
        marginTop="4x"
        href={getSearchUrl({}, locale)}
        onPress={() => {
          storeCrossPageAnalyticsEvent(
            new SearchResultsCtaClick({
              serviceTypeSlug: ServiceTypeSlugEnum['dog-boarding'],
              layoutType: SearchResultLayoutType.CAROUSEL,
            })
          );
        }}
      >
        <Trans>View more sitters</Trans>
      </Button>
    </Flex>
  );
};

export default HomepageSitterCarousel;
